import { useState, useEffect } from "react";
import Layout2 from "@/components/Layout/Layout2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import LeftBar2 from "@/components/Layout/LeftBar2";
import {
  FileSpreadsheet,
  CheckCircle,
  Circle,
  Trash2,
  AlertCircle,
  CreditCard,
  Bell,
} from "lucide-react";
import { SettingsIcon } from "lucide-react";
import { AiOutlinePound } from "react-icons/ai";
import { TbReplace } from "react-icons/tb";
import { TbTextPlus } from "react-icons/tb";
import { MdOutlineWrapText, MdOutlineFastfood, MdOutlineAccessTime, MdShare, MdClose } from "react-icons/md";
import { Checkbox } from "@/components/ui/checkbox";
import Pagination from "@/components/lib/Pagination";
import useAuth from "@/hooks/useAuth";
import { getNotificationsApi, updateNotificationsReadStatusApi } from "@/api/notifications";
import { Skeleton } from "@/components/ui/skeleton";
import useSettings from "@/hooks/useSettings";

const NOTIFICATIONS_PER_PAGE = 10;

const NotificationSkeleton = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-4 p-3 bg-cblack3 rounded-lg"
      >
        <Skeleton className="h-5 w-5 rounded" />
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-6 rounded" />
      </div>
    ))}
  </div>
);

function AllNotification() {
  const auth = useAuth();
  const restaurant_id = auth?.user?.restaurant_id;

  const [notifications, setNotifications] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const { settings } = useSettings();

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!restaurant_id) return;
      setLoading(true);
      try {
        const response = await getNotificationsApi(restaurant_id, {
          page: currentPage,
          per_page: NOTIFICATIONS_PER_PAGE,
          category: "notification",
          till_id: settings?.take_order?.device_id,
        });
        const data = response?.data?.notifications || [];
        const pagination = response?.data?.pagination;

        setNotifications(
          Array.isArray(data) ? data : []
        );

        // Set pagination info from API response
        if (pagination) {
          setTotalPages(pagination.last_page);
          setTotalNotifications(pagination.total);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [restaurant_id, currentPage]);

  // Handle checkbox selection
  const toggleNotificationSelection = (notificationId) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  // Handle select all
  const toggleSelectAll = () => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      const allIds = new Set(
        notifications.map((notification) => notification.id)
      );
      setSelectedNotifications(allIds);
    }
  };

  // Mark selected as read/unread
  const handleMarkAsRead = async (isRead) => {
    if (selectedNotifications.size === 0) return;

    setMarkingAsRead(true);
    try {
      const payload = {
        ids: Array.from(selectedNotifications),
        is_read: isRead,
      };
      await updateNotificationsReadStatusApi(restaurant_id, payload);

      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          selectedNotifications.has(notif.id)
            ? { ...notif, is_read: isRead }
            : notif
        )
      );

      setSelectedNotifications(new Set());
    } catch (error) {
      console.error("Error updating notification status:", error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  // Delete selected notifications
  const handleDeleteSelected = async () => {
    if (selectedNotifications.size === 0) return;

    try {
      // Call delete API if available, otherwise just update UI
      setNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notif) => !selectedNotifications.has(notif.id)
        )
      );
      setSelectedNotifications(new Set());
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  const getLondonTimeParts = (dateInput) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(dateInput);
    const year = parts.find((p) => p.type === "year").value;
    const month = parts.find((p) => p.type === "month").value;
    const day = parts.find((p) => p.type === "day").value;
    const hour = parts.find((p) => p.type === "hour").value;
    const minute = parts.find((p) => p.type === "minute").value;
    const second = parts.find((p) => p.type === "second").value;
    return new Date(year, month - 1, day, hour, minute, second);
  };

  const parseAsLondonTime = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      return getLondonTimeParts(dateValue);
    }
    const str = String(dateValue).trim();
    const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(str);
    if (hasTimezone) {
      const parsedDate = new Date(str);
      if (Number.isNaN(parsedDate.getTime())) return null;
      return getLondonTimeParts(parsedDate);
    } else {
      const normalizedStr = str.replace("T", " ");
      const match = normalizedStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2}) (\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [_, year, month, day, hour, minute, second] = match;
        return new Date(year, month - 1, day, hour, minute, second);
      } else {
        const parsedDate = new Date(str);
        if (Number.isNaN(parsedDate.getTime())) return null;
        return parsedDate;
      }
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Recently";
    const eventDate = parseAsLondonTime(timestamp);
    if (!eventDate) return "Recently";

    const nowLondon = getLondonTimeParts(new Date());
    const diffMs = nowLondon.getTime() - eventDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return eventDate.toLocaleDateString();
  };
  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "All Notifications" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Notifications", path: "/backoffice/notification" },
        { name: "General Notifications", path: "/backoffice/notification" },
      ]}

    >
      <div className="py-6 px-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-cwhite">Notifications</h2>
          <div className="text-sm text-cwhite3">
            Total: {totalNotifications}
          </div>
        </div>

        {/* Action Bar */}
        {selectedNotifications.size > 0 && (
          <div className="bg-cblack3 border border-cyellow rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-cwhite font-medium">
                {selectedNotifications.size} selected
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => handleMarkAsRead(true)}
                  disabled={markingAsRead}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-cwhite rounded-lg text-sm disabled:opacity-50"
                >
                  <CheckCircle size={16} />
                  Mark as Read
                </button>
                <button
                  onClick={() => handleMarkAsRead(false)}
                  disabled={markingAsRead}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-cwhite rounded-lg text-sm disabled:opacity-50"
                >
                  <Circle size={16} />
                  Mark as Unread
                </button>
                {/* <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-cwhite rounded-lg text-sm"
                >
                  <Trash2 size={16} />
                  Delete
                </button> */}
              </div>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="bg-cblack3 rounded-lg border border-cwhite/10 overflow-hidden">
          {loading ? (
            <div className="p-6">
              <NotificationSkeleton />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="text-cwhite3 mb-3" size={48} />
              <h3 className="text-cwhite font-semibold mb-1">
                No Notifications
              </h3>
              <p className="text-cwhite3 text-sm">
                You're all caught up! No notifications at this time.
              </p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="border-b border-cwhite/10">
                <div className="flex items-center gap-4 px-6 py-4 bg-cblack2">
                  <Checkbox
                    checked={
                      selectedNotifications.size === notifications.length &&
                      notifications.length > 0
                    }
                    onCheckedChange={toggleSelectAll}
                    className="h-5 w-5"
                  />
                  <div className="flex-1">
                    <span className="text-cwhite font-semibold text-sm">
                      Message
                    </span>
                  </div>
                  <div className="w-24 text-right">
                    <span className="text-cwhite font-semibold text-sm">
                      Time
                    </span>
                  </div>
                  <div className="w-12 text-right">
                    <span className="text-cwhite font-semibold text-sm">
                      Status
                    </span>
                  </div>
                </div>
              </div>

              {/* Notification Rows */}
              <div className="divide-y divide-cwhite/10">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex items-center gap-4 px-6 py-4 hover:bg-cblack2 transition-colors ${notification.is_read ? "opacity-60" : ""
                      }`}
                  >
                    <Checkbox
                      checked={selectedNotifications.has(notification.id)}
                      onCheckedChange={() =>
                        toggleNotificationSelection(notification.id)
                      }
                      className="h-5 w-5"
                    />

                    {/* Icon and Content */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="bg-color4 p-3 rounded-full flex-shrink-0">
                        {
                          (() => {
                            switch (notification?.type) {
                              case "order_created":
                                return <TbTextPlus size={24} className="text-cblack3" />;
                              case "order_updated":
                                return <MdOutlineWrapText size={22} className="text-cblack3" />;
                              case "order_shared_to_tills":
                                return <MdShare size={22} className="text-cblack3" />;
                              case "order_item_replaced":
                              case "order_replaced":
                              case "order_complementary_updated":
                                return <TbReplace size={22} className="text-cblack3" />;
                              case "order_payment_received":
                              case "order_pay_by_link_paid":
                              case "reservation_deposit_paid":
                                return <AiOutlinePound size={28} className="text-cblack3" />;
                              case "settings_updated":
                                return <SettingsIcon className="text-cblack3" />;
                              case "order_kitchen_ready":
                              case "order_completed":
                              case "pre_order_ongoing":
                                return <MdOutlineFastfood size={22} className="text-cblack3" />;
                              case "order_cooking_time_updated":
                                return <MdOutlineAccessTime size={26} className="text-cblack3" />;
                              case "order_delayed":
                                return <MdOutlineAccessTime size={22} className="text-cblack3" />;
                              case "order_completed_unpaid":
                                return <AiOutlinePound size={22} className="text-cblack3" />;
                              case "reservation_rejected":
                                return <MdClose size={24} className="text-cblack3" />;
                              case "c2c_gift_card_payment_received":
                              case "c2c_gift_card_payment_cancelled":
                              case "c2c_gift_card_payment_failed":
                                return <CreditCard size={22} className="text-cblack3" />;
                              default:
                                return <Bell size={22} className="text-cblack3" />;
                            }
                          })()
                        }
                      </div>
                      <div className="flex flex-col gap-1">
                        <h3 className="text-cwhite font-medium text-sm">
                          {notification.message || "Order Update"}
                        </h3>
                        {notification.description && (
                          <p className="text-cwhite3 text-xs">
                            {notification.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Time */}
                    <div className="w-24 text-right">
                      <span className="text-cwhite3 text-xs">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </div>

                    {/* Status Badge */}
                    <div className="w-12 text-right">
                      {notification.is_read ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cwhite/10">
                          <CheckCircle
                            size={16}
                            className="text-cyellow"
                          />
                        </span>
                      ) : (
                        <span className="inline-flex w-3 h-3 rounded-full bg-cyellow"></span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-cwhite/10 px-6 py-4 flex justify-end">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary Stats */}
        {totalNotifications > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-cblack3 border border-cwhite/10 rounded-lg p-4">
              <div className="text-cwhite3 text-xs font-semibold mb-2">
                TOTAL
              </div>
              <div className="text-2xl font-bold text-cwhite">
                {totalNotifications}
              </div>
            </div>
            <div className="bg-cblack3 border border-cwhite/10 rounded-lg p-4">
              <div className="text-cwhite3 text-xs font-semibold mb-2">
                UNREAD
              </div>
              <div className="text-2xl font-bold text-cyellow">
                {notifications.filter((n) => !n.is_read).length}
              </div>
            </div>
            <div className="bg-cblack3 border border-cwhite/10 rounded-lg p-4">
              <div className="text-cwhite3 text-xs font-semibold mb-2">
                READ
              </div>
              <div className="text-2xl font-bold text-cwhite">
                {notifications.filter((n) => n.is_read).length}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout2>
  );
}

export default AllNotification;
